import { useEffect, useMemo, useState } from "react";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";
import { useTable, useFilters } from "react-table";

import { useTheme } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import IconButton from "@mui/material/IconButton";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import DefaultLineChart from "examples/Charts/LineCharts/DefaultLineChart";
import MDButton from "components/MDButton";

import DialogClienteContactos from "pages/components/DialogClienteContactos";
import DialogObjetivoV2SubmarcaFaturas from "pages/components/DialogObjetivoV2SubmarcaFaturas";
import createAutocompleteFilter from "pages/utils/autocompleteFilter";

const DEFAULT_MAX_FATURAS = 20;

const mesesLabels = [
  "Jan",
  "Fev",
  "Mar",
  "Abr",
  "Mai",
  "Jun",
  "Jul",
  "Ago",
  "Set",
  "Out",
  "Nov",
  "Dez",
];

export default function ClienteDetalheConteudo({
  entidade,
  ativo = true,
  openContactos = false,
  onCloseContactos,
}) {
  const { showAlert } = useAlert();
  const { temFuncao, userData } = useAuth();
  const theme = useTheme();

  const [loading, setLoading] = useState(false); // tabela
  const [linhas, setLinhas] = useState([]);
  const [maxFaturas, setMaxFaturas] = useState(DEFAULT_MAX_FATURAS);

  // preços por material (apenas alguns perfis)
  const [precosOpen, setPrecosOpen] = useState(false);
  const [precosLoading, setPrecosLoading] = useState(false);
  const [precos, setPrecos] = useState(null);
  const [precosRefMat, setPrecosRefMat] = useState("");

  // dialog de linhas da fatura
  const [faturaOpen, setFaturaOpen] = useState(false);
  const [faturaSelecionada, setFaturaSelecionada] = useState("");
  const [faturaLinhas, setFaturaLinhas] = useState([]);

  // gráfico
  const [vendasResumo, setVendasResumo] = useState([]);
  const [loadingResumo, setLoadingResumo] = useState(false);

  const [fabricantesVendas, setFabricantesVendas] = useState([]);
  const [fabricanteVendasSel, setFabricanteVendasSel] = useState(null);

  const [materiaisVendas, setMateriaisVendas] = useState([]);
  const [materialVendasSel, setMaterialVendasSel] = useState(null);

  const [loadingFiltrosVendas, setLoadingFiltrosVendas] = useState(false);

  const [resumoContactos, setResumoContactos] = useState(null);
  const [loadingContactosResumo, setLoadingContactosResumo] = useState(false);

  const [debitosCliente, setDebitosCliente] = useState([]);
  const [loadingDebitosCliente, setLoadingDebitosCliente] = useState(false);

  const [objetivosSubmarcas, setObjetivosSubmarcas] = useState([]);
  const [loadingObjetivosSubmarcas, setLoadingObjetivosSubmarcas] = useState(false);

  const [submarcaFaturasOpen, setSubmarcaFaturasOpen] = useState(false);
  const [submarcaFaturasDetalhe, setSubmarcaFaturasDetalhe] = useState(null);

  const [metricaVendas, setMetricaVendas] = useState("volume");

  const colorMap = {
    warning: theme.palette.warning.main,
    primary: theme.palette.primary.main,
    secondary: theme.palette.secondary.main,
  };

  // Quem pode ver valores monetários
  const podeVerValores =
    temFuncao("Administração") ||
    temFuncao("Informática") ||
    temFuncao("Tesouraria") ||
    temFuncao("Comercial");

  // Quem pode ver o botão VER PREÇOS (igual a podeVerValores mas SEM Comercial)
  const podeVerPrecos =
    temFuncao("Administração") || temFuncao("Informática") || temFuncao("Tesouraria");

  // refvendedor do utilizador corrente (se for Comercial)
  const refVendedorAtual = temFuncao("Comercial") ? (userData?.refvendedor || "").trim() : "";

  const filterFabricantes = useMemo(
    () => createAutocompleteFilter((option) => `${option?.ref ?? ""} ${option?.nome ?? ""}`),
    []
  );

  const filterMateriais = useMemo(
    () => createAutocompleteFilter((option) => `${option?.ref ?? ""} ${option?.nome ?? ""}`),
    []
  );

  // -------------------- Carregar dados --------------------
  useEffect(() => {
    let active = true;

    const carregar = async () => {
      if (!ativo || !entidade) return;

      const refentidade = (entidade.ref || entidade.refentidade || "").trim();
      if (!refentidade) return;

      // reset locais
      setResumoContactos(null);

      setDebitosCliente([]);

      setLoadingContactosResumo(true);

      setLoadingDebitosCliente(true);

      // ---- 1.5) resumo compacto de contactos ----
      const [dadosResumoContactos, errResumoContactos] = await handleApi(
        api.post("Comercial/SelResumoContactos", {
          refcliente: refentidade,
          refvendedor: refVendedorAtual,
        })
      );

      const [dadosDebitosCliente, errDebitosCliente] = await handleApi(
        api.post("Comercial/SelClienteDebitosDialog", {
          refcliente: refentidade,
          refvendedor: refVendedorAtual,
        })
      );

      if (!active) return;

      setLoadingContactosResumo(false);

      setLoadingDebitosCliente(false);

      if (errResumoContactos) {
        showAlert(errResumoContactos, "error");
        setResumoContactos(null);
      } else {
        const row =
          dadosResumoContactos && typeof dadosResumoContactos === "object"
            ? dadosResumoContactos
            : dadosResumoContactos?.data && typeof dadosResumoContactos.data === "object"
            ? dadosResumoContactos.data
            : null;

        setResumoContactos(row);
      }

      if (errDebitosCliente) {
        showAlert(errDebitosCliente, "error");
        setDebitosCliente([]);
      } else {
        const rows = Array.isArray(dadosDebitosCliente)
          ? dadosDebitosCliente
          : Array.isArray(dadosDebitosCliente?.data)
          ? dadosDebitosCliente.data
          : [];

        setDebitosCliente(rows);
      }
    };

    if (ativo && entidade) {
      carregar();
    } else if (!ativo) {
      setLinhas([]);
      setVendasResumo([]);
      setResumoContactos(null);

      setDebitosCliente([]);

      setLoading(false);
      setLoadingResumo(false);
      setLoadingContactosResumo(false);
      setLoadingDebitosCliente(false);
    }

    return () => {
      active = false;
    };
  }, [ativo, entidade, refVendedorAtual, showAlert, temFuncao]);

  // -------------------- Objetivos V2 SUBMARCAS --------------------
  useEffect(() => {
    let active = true;

    const carregarObjetivosSubmarcas = async () => {
      if (!ativo || !entidade) {
        setObjetivosSubmarcas([]);
        setLoadingObjetivosSubmarcas(false);
        return;
      }

      const refcliente = String(entidade.ref || entidade.refentidade || "").trim();

      if (!refcliente) {
        setObjetivosSubmarcas([]);
        setLoadingObjetivosSubmarcas(false);
        return;
      }

      setLoadingObjetivosSubmarcas(true);

      const [dados, err] = await handleApi(
        api.post("Comercial/SelObjetivoV2SubmarcasCliente", {
          refcliente,
          idutilizador: userData?.id || 0,
        })
      );

      if (!active) return;

      setLoadingObjetivosSubmarcas(false);

      if (err) {
        showAlert(err, "error");
        setObjetivosSubmarcas([]);
        return;
      }

      setObjetivosSubmarcas(Array.isArray(dados) ? dados : []);
    };

    carregarObjetivosSubmarcas();

    return () => {
      active = false;
    };
  }, [ativo, entidade, userData?.id, showAlert]);

  // -------------------- Últimas faturas de vendas --------------------
  useEffect(() => {
    let active = true;

    const carregarFaturasVendas = async () => {
      if (!ativo || !entidade) {
        setLinhas([]);
        setLoading(false);
        return;
      }

      const refentidade = (entidade.ref || entidade.refentidade || "").trim();

      if (!refentidade) {
        setLinhas([]);
        setLoading(false);
        return;
      }

      const n = parseInt(maxFaturas, 10);

      if (!Number.isFinite(n) || n <= 0) {
        setLinhas([]);
        setLoading(false);
        return;
      }

      const reffabricante = (fabricanteVendasSel?.ref || "").toString().trim();
      const refmaterial = (materialVendasSel?.ref || "").toString().trim();

      setLoading(true);

      const [dados, err] = await handleApi(
        api.post("Entidades/SelVendasPorEntidade", {
          refentidade,
          refvendedor: refVendedorAtual,
          idutilizador: userData?.id || 0,
          maxfaturas: n,
          reffabricante,
          refmaterial,
        })
      );

      if (!active) return;

      setLoading(false);

      if (err) {
        showAlert(err, "error");
        setLinhas([]);
        return;
      }

      setLinhas(Array.isArray(dados) ? dados : []);
    };

    carregarFaturasVendas();

    return () => {
      active = false;
    };
  }, [
    ativo,
    entidade,
    maxFaturas,
    refVendedorAtual,
    userData?.id,
    fabricanteVendasSel,
    materialVendasSel,
    showAlert,
  ]);

  // -------------------- Resumo de vendas do gráfico --------------------
  useEffect(() => {
    let active = true;

    const carregarResumoVendas = async () => {
      if (!ativo || !entidade) {
        setVendasResumo([]);
        setLoadingResumo(false);
        return;
      }

      const refentidade = (entidade.ref || entidade.refentidade || "").trim();

      if (!refentidade) {
        setVendasResumo([]);
        setLoadingResumo(false);
        return;
      }

      const reffabricante = (fabricanteVendasSel?.ref || "").toString().trim();
      const refmaterial = (materialVendasSel?.ref || "").toString().trim();

      setLoadingResumo(true);

      const [dadosResumo, errResumo] = await handleApi(
        api.post("Entidades/SelVendasPorEntidadeResumo", {
          refentidade,
          refvendedor: refVendedorAtual,
          idutilizador: userData?.id || 0,
          reffabricante,
          refmaterial,
        })
      );

      if (!active) return;

      setLoadingResumo(false);

      if (errResumo) {
        showAlert(errResumo, "error");
        setVendasResumo([]);
        return;
      }

      setVendasResumo(Array.isArray(dadosResumo) ? dadosResumo : []);
    };

    carregarResumoVendas();

    return () => {
      active = false;
    };
  }, [
    ativo,
    entidade,
    refVendedorAtual,
    userData?.id,
    fabricanteVendasSel,
    materialVendasSel,
    showAlert,
  ]);

  // -------------------- Filtros do gráfico: fabricantes --------------------
  useEffect(() => {
    let active = true;

    const carregarFabricantesVendas = async () => {
      if (!ativo) return;

      setLoadingFiltrosVendas(true);

      const [dados, err] = await handleApi(api.post("Entidades/SelFabricantesClienteCombo", {}));

      if (!active) return;

      setLoadingFiltrosVendas(false);

      if (err) {
        showAlert(err, "error");
        setFabricantesVendas([]);
        return;
      }

      setFabricantesVendas(Array.isArray(dados) ? dados : []);
    };

    carregarFabricantesVendas();

    return () => {
      active = false;
    };
  }, [ativo, showAlert]);

  // -------------------- Filtros do gráfico: materiais --------------------
  useEffect(() => {
    let active = true;

    const carregarMateriaisVendas = async () => {
      if (!ativo) return;

      const reffabricante = (fabricanteVendasSel?.ref || "").toString().trim();

      setLoadingFiltrosVendas(true);
      setMateriaisVendas([]);

      const [dados, err] = await handleApi(
        api.post("Entidades/SelMateriaisClienteCombo", {
          reffabricante,
        })
      );

      if (!active) return;

      setLoadingFiltrosVendas(false);

      if (err) {
        showAlert(err, "error");
        setMateriaisVendas([]);
        return;
      }

      setMateriaisVendas(Array.isArray(dados) ? dados : []);
    };

    carregarMateriaisVendas();

    return () => {
      active = false;
    };
  }, [ativo, fabricanteVendasSel, showAlert]);

  // -------------------- Helpers --------------------

  const formatData = (v) => {
    if (!v) return "";
    const s = String(v);
    return s.substring(0, 10);
  };

  const formatDateTime = (v) => {
    if (!v) return "";
    const dt = new Date(v);
    if (Number.isNaN(dt.getTime())) return "";
    return dt.toLocaleString("pt-PT", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatNum = (v, dec = 2) => {
    if (v === null || v === undefined || v === "") return "";
    const n = Number(v);
    if (!Number.isFinite(n)) return "";
    return n.toLocaleString("pt-PT", {
      minimumFractionDigits: dec,
      maximumFractionDigits: dec,
    });
  };

  const joinValPerc = (val, perc, withEuro = false) => {
    const v = formatNum(val);
    const p = formatNum(perc, 1);
    let s = "";
    if (v) s += `${v}${withEuro ? " €" : ""}`;
    if (p) s += `${s ? " " : ""}(${p}%)`;
    return s;
  };

  const calcularMargem = (precosObj) => {
    if (!precosObj) {
      return { margemValor: null, margemPerc: null, margemTexto: "" };
    }

    const venda = Number(precosObj.zpr0 ?? 0);
    const custo = Number(precosObj.zpvr ?? 0);

    if (!Number.isFinite(venda) || !Number.isFinite(custo) || custo === 0) {
      return { margemValor: null, margemPerc: null, margemTexto: "" };
    }

    const margemValor = venda - custo;
    // margem % calculada sobre o custo (mais intuitivo: 100% = vender ao dobro do custo)
    const margemPerc = (margemValor / custo) * 100;

    return {
      margemValor,
      margemPerc,
      margemTexto: joinValPerc(margemValor, margemPerc, true), // "X,XX € (Y,Y%)"
    };
  };

  const abrirFatura = (faturanumero) => {
    const numero = String(faturanumero || "").trim();
    if (!numero) return;

    let linhasFatura = (linhas || []).filter(
      (x) => String(x?.faturanumero || "").trim() === numero
    );

    if (linhasFatura.length === 0) {
      linhasFatura = (data || []).filter((x) => String(x?.faturanumero || "").trim() === numero);
    }

    setFaturaSelecionada(numero);
    setFaturaLinhas(linhasFatura);
    setFaturaOpen(true);
  };

  const handleChangeMax = (e) => {
    const v = e.target.value;
    // permitimos vazio para o utilizador apagar e escrever de novo
    if (v === "") {
      setMaxFaturas("");
      return;
    }
    // apenas dígitos
    if (!/^\d+$/.test(v)) return;
    setMaxFaturas(v);
  };

  const itemContactos = entidade
    ? {
        cliente: entidade.nome || "",
        pagador: resumoContactos?.pagador_nome || "",
        vendedor: entidade.vendedor || "",
        refcliente: resumoContactos?.refcliente || entidade.ref || entidade.refentidade || "",
        refpagador: resumoContactos?.refpagador || "",
        refvendedor: resumoContactos?.refvendedor || refVendedorAtual || "",
      }
    : null;

  // -------------------- Dados do gráfico (últimos 3 anos, por mês) --------------------

  const anoAtual = new Date().getFullYear();
  const anosVendas = [anoAtual, anoAtual - 1, anoAtual - 2];

  const vendasPorAno = {};
  anosVendas.forEach((ano) => {
    vendasPorAno[ano] = new Array(12).fill(0);
  });

  (vendasResumo || []).forEach((row) => {
    const ano = Number(row.ano);
    const mes = Number(row.mes); // 1-12

    let valor = 0;

    if (metricaVendas === "valortotal") {
      valor = Number(row.valortotal ?? 0);
    } else if (metricaVendas === "volume") {
      valor = Number(row.volume ?? 0);
    } else {
      valor = Number(row.quantidade ?? row.qtd ?? 0);
    }

    const mesIdx = mes - 1;

    if (!Number.isFinite(valor)) return;
    if (!vendasPorAno[ano]) return;
    if (mesIdx < 0 || mesIdx > 11) return;

    vendasPorAno[ano][mesIdx] += valor;
  });

  // Formatação dos valores do gráfico:
  // quantidades -> inteiros
  // litros/euros -> mantém decimais
  Object.keys(vendasPorAno).forEach((anoStr) => {
    const ano = Number(anoStr);

    vendasPorAno[ano] = (vendasPorAno[ano] || []).map((v) => {
      if (v === null || typeof v !== "number" || Number.isNaN(v)) return v;

      if (metricaVendas === "quantidade") {
        return Math.round(v);
      }

      return v;
    });
  });

  const mesAtual = new Date().getMonth(); // 0-11
  for (let m = mesAtual + 1; m < 12; m += 1) {
    if (vendasPorAno[anoAtual]) {
      vendasPorAno[anoAtual][m] = null;
    }
  }

  const vendasDatasets = anosVendas.map((ano, idx) => ({
    label: String(ano),
    data: vendasPorAno[ano],
    color:
      idx === 0
        ? "warning" // ano corrente
        : idx === 1
        ? "primary" // ano anterior
        : "secondary", // antepenúltimo
  }));

  const vendasDatasetsFiltrados = vendasDatasets.filter((ds) =>
    (ds.data || []).some((v) => Number.isFinite(v) && v !== 0)
  );

  const vendasTotaisAno = vendasDatasetsFiltrados.reduce((acc, ds) => {
    const total = (ds.data || []).reduce((s, v) => (Number.isFinite(v) ? s + v : s), 0);
    acc[ds.label] = total;
    return acc;
  }, {});

  const temVendasResumo = vendasDatasetsFiltrados.length > 0;

  const vendasChart = {
    labels: mesesLabels,
    datasets: vendasDatasetsFiltrados.map((ds) => ({
      label: ds.label,
      data: ds.data,
      color: ds.color,
    })),
  };

  // -------------------- react-table (dados + colunas) --------------------

  const data = useMemo(() => {
    return (linhas || []).map((row) => {
      const qtd = row.qtd ?? row.quantidade;
      const qtdStr = formatNum(qtd, 2);
      const unidade = row.qtdunid || "";

      return {
        data: formatData(row.faturadata),
        faturanumero: row.faturanumero ?? "",
        item: row.item ?? "",
        itemTipo: row.catitemnome ?? "",
        refMat: row.refmaterial ?? "",
        nomeMat: row.nomematerial ?? "",
        qtdUnid: `${qtdStr}${unidade ? ` (${unidade})` : ""}`,
        precoAcons: formatNum(row.precoaconselhado),
        descCom: joinValPerc(row.desccomvalor, row.desccomperc),
        descProm: joinValPerc(row.descpromvalor, row.descpromperc),
        precoLiq: formatNum(row.precoliquido),
        iva: joinValPerc(row.ivavalor, row.ivaperc),
        precoTotal: formatNum(row.precototal),
        fabricante: row.fabricante ?? "",
      };
    });
  }, [linhas]);

  const columns = useMemo(() => {
    const base = [
      {
        Header: "",
        id: "acoes",
        disableFilters: true,
        width: 110,
        Cell: ({ row }) => (
          <MDButton
            color="warning"
            onClick={() => abrirFatura(row.original.faturanumero)}
            sx={{ flex: "0 0 auto" }}
          >
            VER FATURA
          </MDButton>
        ),
      },

      {
        Header: "Data",
        accessor: "data",
      },
      {
        Header: "Nº Fatura",
        accessor: "faturanumero",
      },
      {
        Header: "Item Nº",
        accessor: "item",
        align: "right",
      },
      {
        Header: "Item Tipo",
        accessor: "itemTipo",
      },
      {
        Header: "Refª Mat.",
        accessor: "refMat",
      },
      {
        Header: "Nome do material",
        accessor: "nomeMat",
      },
      {
        Header: "Qtd. (Unid.)",
        accessor: "qtdUnid",
        align: "right",
      },
    ];

    if (podeVerValores) {
      base.push(
        {
          Header: () => (
            <Tooltip title="Preço Aconselhado" arrow>
              <span>Preço Acons.</span>
            </Tooltip>
          ),
          accessor: "precoAcons",
          align: "right",
        },
        {
          Header: () => (
            <Tooltip title="Desconto Comercial" arrow>
              <span>Desc. Com.</span>
            </Tooltip>
          ),
          accessor: "descCom",
          align: "right",
        },
        {
          Header: () => (
            <Tooltip title="Desconto Promocional" arrow>
              <span>Desc. Prom.</span>
            </Tooltip>
          ),
          accessor: "descProm",
          align: "right",
        },
        {
          Header: "Preço Líq.",
          accessor: "precoLiq",
          align: "right",
        },
        {
          Header: "IVA",
          accessor: "iva",
          align: "right",
        },
        {
          Header: "Preço Total",
          accessor: "precoTotal",
          align: "right",
        }
      );
    }

    base.push({
      Header: "Fabricante",
      accessor: "fabricante",
    });

    return base;
  }, [podeVerValores, podeVerPrecos, linhas, data]);

  const defaultColumn = useMemo(
    () => ({
      Filter: DefaultColumnFilter,
    }),
    []
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data,
      defaultColumn,
    },
    useFilters
  );

  const { margemTexto } = calcularMargem(precos);

  return (
    <>
      <div
        style={{
          padding: "16px 20px 20px 20px",
        }}
      >
        {/* ================== SEGMENTAÇÃO / BLOQUEIOS ================== */}
        <div
          style={{
            marginBottom: 16,
            padding: "10px 12px",
            border: "1px solid #eee",
            borderRadius: 8,
            backgroundColor: "#fafafa",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 16,
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 12,
                  color: "#7b809a",
                  fontWeight: 600,
                  marginBottom: 6,
                }}
              >
                Segmentação
              </div>

              <div style={{ fontSize: 13, lineHeight: 1.55 }}>
                <div>
                  <strong>Potencial:</strong> {entidade?.segmentopotencial || "-"}
                </div>
                <div>
                  <strong>Nível 1:</strong> {entidade?.segmenton1 || "-"}
                </div>
                <div>
                  <strong>Nível 2:</strong> {entidade?.segmenton2 || "-"}
                </div>
                <div>
                  <strong>Nível 3:</strong> {entidade?.segmenton3 || "-"}
                </div>
              </div>
            </div>

            <div>
              <div
                style={{
                  fontSize: 12,
                  color: "#7b809a",
                  fontWeight: 600,
                  marginBottom: 6,
                }}
              >
                Bloqueios
              </div>

              <div style={{ fontSize: 13, lineHeight: 1.55 }}>
                <div>
                  <strong>Fornecimento:</strong> {entidade?.refbloqueiofornecimento || "-"}
                </div>
                <div>
                  <strong>Faturação:</strong> {entidade?.refbloqueiofaturacao || "-"}
                </div>
                <div>
                  <strong>Ordem:</strong> {entidade?.refbloqueioordem || "-"}
                </div>
                <div>
                  <strong>Entrega facilitada:</strong>{" "}
                  {Number(entidade?.tementregafacilitada || 0) === 1 ? "Sim" : "Não"}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: 16,
            flexWrap: "wrap",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <Autocomplete
            options={fabricantesVendas}
            value={fabricanteVendasSel}
            loading={loadingFiltrosVendas}
            filterOptions={filterFabricantes}
            getOptionLabel={(option) =>
              option ? `${option.ref ?? ""} - ${option.nome ?? ""}`.trim() : ""
            }
            isOptionEqualToValue={(option, value) => option?.ref === value?.ref}
            onChange={(_, novoFabricante) => {
              setFabricanteVendasSel(novoFabricante);
              setMaterialVendasSel(null);
            }}
            sx={{ minWidth: 300, flex: "1 1 300px" }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Fabricante"
                placeholder="Todos os fabricantes"
                size="small"
              />
            )}
          />

          <Autocomplete
            options={materiaisVendas}
            value={materialVendasSel}
            loading={loadingFiltrosVendas}
            filterOptions={filterMateriais}
            getOptionLabel={(option) =>
              option ? `${option.ref ?? ""} - ${option.nome ?? ""}`.trim() : ""
            }
            isOptionEqualToValue={(option, value) => option?.ref === value?.ref}
            onChange={(_, novoMaterial) => {
              setMaterialVendasSel(novoMaterial);
            }}
            sx={{ minWidth: 300, flex: "1 1 300px" }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Material"
                placeholder="Todos os materiais"
                size="small"
              />
            )}
          />
        </div>

        <div style={{ marginBottom: 10 }}>
          <div
            style={{
              fontSize: 12,
              color: "#7b809a",
              marginBottom: 4,
              fontWeight: 600,
            }}
          >
            Métrica
          </div>

          <RadioGroup
            row
            value={metricaVendas}
            onChange={(e) => setMetricaVendas(e.target.value)}
            sx={{ "& .MuiTypography-root": { fontSize: 12 } }}
          >
            <FormControlLabel value="valortotal" control={<Radio size="small" />} label="Euros" />

            <FormControlLabel value="volume" control={<Radio size="small" />} label="Litros" />

            <FormControlLabel
              value="quantidade"
              control={<Radio size="small" />}
              label="Quantidades"
            />
          </RadioGroup>
        </div>

        {/* ================== GRÁFICO ================== */}
        <h6 style={{ marginTop: 0, marginBottom: 8, fontSize: 13, color: "#7b809a" }}>
          Vendas da entidade (
          {metricaVendas === "valortotal"
            ? "euros"
            : metricaVendas === "volume"
            ? "litros"
            : "quantidades"}
          , nos últimos 3 anos)
        </h6>

        {loadingResumo ? (
          <div
            style={{
              padding: "16px 0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={24} />
          </div>
        ) : !temVendasResumo ? (
          <div style={{ fontSize: 13, color: "#555", marginBottom: 16 }}>
            Não há vendas registadas para esta entidade nos últimos 3 anos
            {temFuncao("Comercial") && refVendedorAtual ? " com o seu código de vendedor." : "."}
          </div>
        ) : (
          <div style={{ marginBottom: 20 }}>
            {/* legenda das cores/anos */}
            <div
              style={{
                fontSize: 12,
                color: "#7b809a",
                marginBottom: 8,
                display: "flex",
                flexWrap: "wrap",
                gap: 20,
              }}
            >
              {vendasDatasetsFiltrados.map((ds) => (
                <span key={ds.label} style={{ display: "inline-flex", alignItems: "center" }}>
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      marginRight: 6,
                      backgroundColor: colorMap[ds.color] || "#999",
                    }}
                  />
                  {ds.label}{" "}
                  <span style={{ color: "#3a3a3a", marginLeft: 4 }}>
                    (
                    {metricaVendas === "valortotal"
                      ? "Total euros: "
                      : metricaVendas === "volume"
                      ? "Total litros: "
                      : "Qtd. total: "}
                    {Number(vendasTotaisAno[ds.label]).toLocaleString("pt-PT", {
                      minimumFractionDigits: metricaVendas === "quantidade" ? 0 : 2,
                      maximumFractionDigits: metricaVendas === "quantidade" ? 0 : 2,
                    })}
                    {metricaVendas === "valortotal" ? " €" : ""})
                  </span>
                </span>
              ))}
            </div>

            <DefaultLineChart title="" description="" chart={vendasChart} />
          </div>
        )}

        {/* ================== OBJETIVOS V2 SUBMARCAS ================== */}
        {loadingObjetivosSubmarcas ? (
          <div
            style={{
              padding: "6px 0 14px 0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={20} />
          </div>
        ) : objetivosSubmarcas.length > 0 ? (
          <div style={{ marginBottom: 16 }}>
            {objetivosSubmarcas.map((objetivo) => {
              const submarcasOrigem = Array.isArray(objetivo?.submarcas) ? objetivo.submarcas : [];

              const submarcas = Object.values(
                submarcasOrigem.reduce((acc, sm) => {
                  const nome = String(sm?.nome || sm?.refsubmarca || "").trim();
                  const chave = nome.toLocaleUpperCase("pt-PT");

                  if (!acc[chave]) {
                    acc[chave] = {
                      ...sm,
                      nome,
                      refsSubmarca: [sm.refsubmarca],
                    };
                  } else {
                    if (sm?.refsubmarca && !acc[chave].refsSubmarca.includes(sm.refsubmarca)) {
                      acc[chave].refsSubmarca.push(sm.refsubmarca);
                    }

                    if (Number(sm?.temsubmarca || 0) === 1) {
                      acc[chave].temsubmarca = 1;
                    }
                  }

                  return acc;
                }, {})
              );

              const dataIni = objetivo?.dataini
                ? new Date(objetivo.dataini).toLocaleDateString("pt-PT")
                : "";

              const dataFim = objetivo?.datafim
                ? new Date(objetivo.datafim).toLocaleDateString("pt-PT")
                : "";

              return (
                <Accordion
                  key={objetivo.idobjetivo}
                  disableGutters
                  sx={{
                    mb: 1,
                    border: "1px solid #eee",
                    boxShadow: "none",
                    "&:before": {
                      display: "none",
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      minHeight: 46,
                      "& .MuiAccordionSummary-content": {
                        margin: "10px 0",
                      },
                    }}
                  >
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#344767",
                      }}
                    >
                      {objetivo.nome || `Objetivo ${objetivo.idobjetivo}`}
                      {dataIni && dataFim ? ` · ${dataIni} a ${dataFim}` : ""}
                    </div>
                  </AccordionSummary>

                  <AccordionDetails sx={{ pt: 0 }}>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                        gap: 8,
                      }}
                    >
                      {submarcas.map((sm) => {
                        const tem = Number(sm?.temsubmarca || 0) === 1;

                        return (
                          <div
                            key={sm.refsubmarca}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 10,
                              padding: "8px 10px",
                              border: "1px solid #eee",
                              borderRadius: 6,
                              backgroundColor: "#fafafa",
                              fontSize: 12,
                            }}
                          >
                            <span>{sm.nome || sm.refsubmarca}</span>

                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              <span
                                style={{
                                  fontSize: 18,
                                  lineHeight: 1,
                                  color: tem ? "#1a73e8" : "#7b809a",
                                  fontWeight: tem ? 700 : 400,
                                }}
                              >
                                {tem ? "✔" : "—"}
                              </span>

                              {tem && (
                                <Tooltip title="Ver faturas" arrow>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setSubmarcaFaturasDetalhe({
                                        idobjetivo: objetivo.idobjetivo,
                                        refcliente: String(
                                          entidade?.ref || entidade?.refentidade || ""
                                        ).trim(),
                                        refsubmarca: sm.refsubmarca,
                                        refssubmarca: Array.isArray(sm.refsSubmarca)
                                          ? sm.refsSubmarca
                                          : [sm.refsubmarca],
                                        submarca: sm.nome || sm.refsubmarca,
                                      });

                                      setSubmarcaFaturasOpen(true);
                                    }}
                                    sx={{
                                      p: 0.5,
                                      color: "#1a73e8",
                                    }}
                                  >
                                    <InfoOutlinedIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </div>
        ) : null}

        {loadingDebitosCliente ? (
          <div
            style={{
              padding: "6px 0 14px 0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={20} />
          </div>
        ) : debitosCliente.length > 0 ? (
          <div
            style={{
              marginBottom: 16,
              padding: "10px 12px",
              border: "1px solid #eee",
              borderRadius: 8,
              backgroundColor: "#fafafa",
              overflowX: "auto",
            }}
          >
            <div
              style={{
                fontSize: 12,
                color: "#7b809a",
                marginBottom: 10,
                fontWeight: 600,
              }}
            >
              Débitos do cliente
            </div>

            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 12,
                minWidth: 1100,
              }}
            >
              <thead>
                <tr>
                  <th style={{ ...thStyle, color: "#344767" }}>Cliente</th>
                  <th style={{ ...thStyle, color: "#344767" }}>Pagador</th>
                  <th style={{ ...thStyle, color: "#344767" }}>Cond. Pagamento</th>
                  <th style={{ ...thStyle, textAlign: "right", color: "#344767" }}>
                    Saldo Vencido
                  </th>
                  <th style={{ ...thStyle, textAlign: "right", color: "#344767" }}>A Vencer</th>
                  <th style={{ ...thStyle, textAlign: "right", color: "#344767" }}>Saldo Total</th>
                  <th style={{ ...thStyle, textAlign: "right", color: "#344767" }}>1 a 30 dias</th>
                  <th style={{ ...thStyle, textAlign: "right", color: "#344767" }}>31 a 60 dias</th>
                  <th style={{ ...thStyle, textAlign: "right", color: "#344767" }}>61 a 90 dias</th>
                  <th style={{ ...thStyle, textAlign: "right", color: "#344767" }}>
                    91 a 120 dias
                  </th>
                  <th style={{ ...thStyle, textAlign: "right", color: "#344767" }}>
                    121 a 150 dias
                  </th>
                  <th style={{ ...thStyle, textAlign: "right", color: "#344767" }}>
                    Mais de 150 dias
                  </th>
                </tr>
              </thead>
              <tbody>
                {debitosCliente.map((row, idx) => (
                  <tr key={idx}>
                    <td style={tdStyle}>
                      {(row?.refcliente || "") + (row?.cliente || "" ? ` - ${row.cliente}` : "")}
                    </td>
                    <td style={tdStyle}>
                      {(row?.refpagador || "") + (row?.pagador || "" ? ` - ${row.pagador}` : "")}
                    </td>
                    <td style={tdStyle}>{row?.condicoespagamento || "-"}</td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>
                      {formatNum(row?.vencidototal)}
                    </td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>{formatNum(row?.avencer)}</td>
                    <td style={{ ...tdStyle, textAlign: "right", fontWeight: 700 }}>
                      {formatNum(row?.saldototal)}
                    </td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>{formatNum(row?.dias_1a30)}</td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>{formatNum(row?.dias_31a60)}</td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>{formatNum(row?.dias_61a90)}</td>
                    <td style={{ ...tdStyle, textAlign: "right", fontWeight: 700 }}>
                      {formatNum(row?.dias_91a120)}
                    </td>
                    <td style={{ ...tdStyle, textAlign: "right", fontWeight: 700 }}>
                      {formatNum(row?.dias_121a150)}
                    </td>
                    <td
                      style={{
                        ...tdStyle,
                        textAlign: "right",
                        fontWeight: 700,
                        color: Number(row?.dias_151mais || 0) > 0 ? "#d32f2f" : undefined,
                      }}
                    >
                      {formatNum(row?.dias_151mais)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {/* ================== RESUMO DE CONTACTOS ================== */}
        {loadingContactosResumo ? (
          <div
            style={{
              padding: "8px 0 14px 0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={20} />
          </div>
        ) : (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, lineHeight: 1.45 }}>
              <div>
                <strong>Pagador:</strong>{" "}
                {resumoContactos?.refpagador
                  ? `${resumoContactos.refpagador}${
                      resumoContactos?.pagador_nome ? ` - ${resumoContactos.pagador_nome}` : ""
                    }`
                  : "-"}
              </div>

              <div>
                <strong>Último contacto:</strong>{" "}
                {resumoContactos?.data_ultimo_contacto
                  ? formatDateTime(resumoContactos.data_ultimo_contacto)
                  : "-"}
              </div>

              <div
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "100%",
                }}
                title={resumoContactos?.obs_ultimo_contacto || ""}
              >
                <strong>Obs.:</strong> {resumoContactos?.obs_ultimo_contacto || "-"}
              </div>

              <div>
                <strong>Próx. agendamento:</strong>{" "}
                {resumoContactos?.data_proximo_agendamento
                  ? formatDateTime(resumoContactos.data_proximo_agendamento)
                  : "-"}
              </div>
            </div>
          </div>
        )}

        {/* Linha com nº de faturas */}
        <div
          style={{
            marginBottom: 8,
            fontSize: 12,
            color: "#7b809a",
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <span>
            Apresentadas as últimas
            <span style={{ marginLeft: 5, marginRight: 5 }}>
              <input
                type="text"
                value={maxFaturas}
                onChange={handleChangeMax}
                style={{
                  width: 40,
                  padding: "6px 6px",
                  fontSize: 12,
                  borderRadius: 4,
                  border: "1px solid #ccc",
                  textAlign: "center",
                }}
              />
            </span>
            faturas de vendas (todas as linhas, ordenadas por data desc.).
          </span>
        </div>

        {/* separador visual entre gráfico e tabela */}
        <div
          style={{
            height: 1,
            backgroundColor: "#eee",
            marginBottom: 8,
          }}
        />

        {/* ================== TABELA ================== */}
        {loading ? (
          <div
            style={{
              padding: "24px 0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={26} />
          </div>
        ) : linhas.length === 0 ? (
          <div style={{ fontSize: 13, color: "#555" }}>
            Não foram encontradas faturas de vendas para esta entidade
            {temFuncao("Comercial") && refVendedorAtual ? " com o seu código de vendedor." : "."}
          </div>
        ) : (
          <div
            style={{
              maxHeight: 420,
              overflowY: "auto",
              overflowX: "auto",
            }}
          >
            <table
              {...getTableProps()}
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 13,
                minWidth: 900,
              }}
            >
              <thead>
                {headerGroups.map((headerGroup) => (
                  <tr key={headerGroup.id} {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th
                        key={column.id}
                        {...column.getHeaderProps()}
                        style={{
                          ...thStyle,
                          textAlign: column.align || "left",
                          position: "sticky",
                          top: 0,
                          zIndex: 2,
                          backgroundColor: "#f8f9fa",
                        }}
                      >
                        <div>{column.render("Header")}</div>
                        {column.canFilter && (
                          <div style={{ marginTop: 4 }}>{column.render("Filter")}</div>
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {rows.map((row) => {
                  prepareRow(row);
                  return (
                    <tr key={row.id} {...row.getRowProps()}>
                      {row.cells.map((cell) => (
                        <td
                          key={cell.column.id}
                          {...cell.getCellProps()}
                          style={{
                            ...tdStyle,
                            textAlign: cell.column.align || "left",
                          }}
                        >
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Dialog de preços por material (apenas se tiver permissão) */}
      <Dialog open={precosOpen} onClose={() => setPrecosOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Preços do material {precosRefMat ? `(${precosRefMat})` : ""}</DialogTitle>

        <DialogContent dividers>
          {precosLoading ? (
            <div style={{ padding: "24px 0", textAlign: "center" }}>
              <CircularProgress size={26} />
            </div>
          ) : !precos ? (
            <div style={{ fontSize: 13, color: "#555" }}>
              Não foram encontrados preços registados para este material.
            </div>
          ) : (
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 13,
                minWidth: 400,
              }}
            >
              <thead>
                <tr>
                  <th style={thStyle}>PV Acons.</th>
                  {podeVerPrecos && <th style={thStyle}>Margem</th>}
                  {podeVerPrecos && <th style={thStyle}>PC</th>}
                </tr>
              </thead>

              <tbody>
                <tr>
                  {/* PV aconselhado (zpr0) */}
                  <td style={tdStyle}>{formatNum(precos.zpr0)}</td>

                  {/* Margem em € e % (calculada a partir de zpr0 e zpvr) */}
                  {podeVerPrecos && <td style={tdStyle}>{margemTexto}</td>}

                  {/* Preço de custo (zpvr, já calculado dinamicamente na API para certos fabricantes) */}
                  {podeVerPrecos && <td style={tdStyle}>{formatNum(precos.zpvr)}</td>}
                </tr>
              </tbody>
            </table>
          )}
        </DialogContent>

        <DialogActions>
          <MDButton
            color="info"
            variant="gradient"
            size="small"
            onClick={() => setPrecosOpen(false)}
          >
            Fechar
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={faturaOpen} onClose={() => setFaturaOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Fatura {faturaSelecionada}</DialogTitle>

        <DialogContent dividers>
          {faturaLinhas.length === 0 ? (
            <div style={{ fontSize: 13, color: "#555" }}>
              Não foram encontradas linhas para esta fatura.
            </div>
          ) : (
            <div
              style={{
                maxHeight: 500,
                overflowY: "auto",
                overflowX: "auto",
              }}
            >
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 13,
                }}
              >
                <thead>
                  <tr>
                    <th style={thStyle}>Ref.</th>
                    <th style={thStyle}>Material</th>
                    <th style={thStyle}>Qtd.</th>
                    <th style={thStyle}>Unid.</th>
                    <th style={thStyle}>Preço Acons.</th>
                    <th style={thStyle}>Desc. Com.</th>
                    <th style={thStyle}>Desc. Prom.</th>
                    <th style={thStyle}>Preço Líq.</th>
                    <th style={thStyle}>IVA</th>
                    <th style={thStyle}>Preço Total</th>
                    <th style={thStyle}>Fabricante</th>
                  </tr>
                </thead>

                <tbody>
                  {faturaLinhas.map((row, idx) => (
                    <tr key={idx}>
                      <td style={tdStyle}>{row.refmaterial || row.refMat}</td>
                      <td style={tdStyle}>{row.nomematerial || row.nomeMat}</td>
                      <td style={{ ...tdStyle, textAlign: "right" }}>
                        {row.qtd !== undefined ? formatNum(row.qtd) : row.qtdUnid}
                      </td>
                      <td style={tdStyle}>{row.qtdunid || ""}</td>
                      <td style={{ ...tdStyle, textAlign: "right" }}>
                        {formatNum(row.precoaconselhado)}
                      </td>
                      <td style={{ ...tdStyle, textAlign: "right" }}>
                        {joinValPerc(row.desccomvalor, row.desccomperc)}
                      </td>
                      <td style={{ ...tdStyle, textAlign: "right" }}>
                        {joinValPerc(row.descpromvalor, row.descpromperc)}
                      </td>
                      <td style={{ ...tdStyle, textAlign: "right" }}>
                        {formatNum(row.precoliquido)}
                      </td>
                      <td style={{ ...tdStyle, textAlign: "right" }}>
                        {joinValPerc(row.ivavalor, row.ivaperc)}
                      </td>
                      <td style={{ ...tdStyle, textAlign: "right" }}>
                        {formatNum(row.precototal)}
                      </td>
                      <td style={tdStyle}>{row.fabricante}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>

        <DialogActions>
          <MDButton
            color="info"
            variant="gradient"
            size="small"
            onClick={() => setFaturaOpen(false)}
          >
            Fechar
          </MDButton>
        </DialogActions>
      </Dialog>

      <DialogObjetivoV2SubmarcaFaturas
        open={submarcaFaturasOpen}
        onClose={() => {
          setSubmarcaFaturasOpen(false);
          setSubmarcaFaturasDetalhe(null);
        }}
        idObjetivo={submarcaFaturasDetalhe?.idobjetivo || 0}
        detalhe={submarcaFaturasDetalhe}
      />

      <DialogClienteContactos
        open={openContactos}
        onClose={async () => {
          if (typeof onCloseContactos === "function") {
            onCloseContactos();
          }

          const refentidade = String(entidade?.ref || entidade?.refentidade || "").trim();

          if (!refentidade) return;

          setLoadingContactosResumo(true);

          const [dadosResumoContactos, errResumoContactos] = await handleApi(
            api.post("Comercial/SelResumoContactos", {
              refcliente: refentidade,
              refpagador: refentidade,
              refvendedor: refVendedorAtual,
            })
          );

          setLoadingContactosResumo(false);

          if (errResumoContactos) {
            showAlert(errResumoContactos, "error");
            return;
          }

          const row =
            dadosResumoContactos && typeof dadosResumoContactos === "object"
              ? dadosResumoContactos
              : dadosResumoContactos?.data && typeof dadosResumoContactos.data === "object"
              ? dadosResumoContactos.data
              : null;

          setResumoContactos(row);
        }}
        item={itemContactos}
      />
    </>
  );
}

// estilos reutilizáveis
const thStyle = {
  textAlign: "left",
  padding: "6px 8px",
  fontSize: 12,
  color: "#7b809a",
  borderBottom: "1px solid #eee",
  whiteSpace: "nowrap",
};

const tdStyle = {
  padding: "8px 10px",
  borderTop: "1px solid #eee",
  fontSize: 13,
};

// filtro padrão (mesmo critério do Playbook)
function DefaultColumnFilter({ column: { filterValue, setFilter } }) {
  return (
    <input
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      style={{
        width: "100%",
        fontSize: 11,
        padding: "2px 4px",
        borderRadius: 4,
        border: "1px solid #ddd",
      }}
      placeholder="filtrar…"
    />
  );
}
